import katex from 'katex';
import 'katex/dist/katex.min.css';
import { RUN_RE, SEUIL_LONGUE, decouperTexte, decouperTex, texVisible } from './expressions';
import { espacerMilliersTexte, espacerMilliersTex } from '../../../utils/milliers';

/**
 * Rend un fragment HTML où les blocs `$...$` sont convertis en KaTeX inline.
 *
 * Regex LENIENT (identique à `MathText.jsx`) : `/\$([^$]+)\$/g`. Couvre
 * `$\dfrac{1}{2}$`, `$OA = 3$`, `$1{,}5$`, etc. `try/catch` laisse le match
 * brut si KaTeX ne sait pas interpréter (cas marginal `$15` monnaie).
 *
 * Usage : via `<HtmlWithKatex html={...} />` qui injecte le résultat en
 * `dangerouslySetInnerHTML`. On reste en HTML string (pas de parsing DOM
 * ni de React tree) pour préserver le markup HTML riche de `payload.texte`.
 *
 * Expressions JAMAIS coupées (2026-09-02, cf. helpers/expressions.js) :
 *   - une expression en texte (« D = 48 − 8 × 10 ÷ 4 ») et une formule
 *     KaTeX inline sont enveloppées dans .ep-formule (white-space:nowrap) :
 *     elles descendent entières à la ligne suivante ;
 *   - au-delà de SEUIL_LONGUE caractères visibles, bloc de lignes coupé au
 *     « = » (.ep-formule-bloc / .ep-formule-ligne), à défaut après + / −.
 * Balises et attributs ne sont jamais touchés : une balise commence par une
 * lettre ou « / », donc « 3 < 5 » en texte n'en est pas une.
 */

const MATH_REGEX = /\$([^$]+)\$/g;
const BALISE_RE = /(<\/?[a-zA-Z][^>]*>)/;
const TEX_RE = /(\$[^$]+\$)/;

const blocLignes = (lignesHtml, indentCh) => `<span class="ep-formule-bloc">${
    lignesHtml.map((l, i) => `<span class="ep-formule-ligne"${
        i > 0 && indentCh ? ` style="padding-left:${indentCh}ch"` : ''
    }>${l}</span>`).join('')
}</span>`;

const htmlRunTexte = (run) => {
    if (run.length <= SEUIL_LONGUE) return `<span class="ep-formule">${run}</span>`;
    const { lignes, indent } = decouperTexte(run);
    return blocLignes(lignes, indent);
};

const katexInline = (tex) => katex.renderToString(tex, {
    throwOnError: false,
    displayMode: false,
});

const htmlTex = (match, texBrut) => {
    // Espaces de milliers (utils/milliers.js) posés sur la source AVANT la
    // découpe : « \, » ne compte pas comme un caractère visible.
    const tex = espacerMilliersTex(texBrut);
    try {
        if (texVisible(tex) <= SEUIL_LONGUE) return `<span class="ep-formule">${katexInline(tex)}</span>`;
        const { lignes, indent } = decouperTex(tex);
        return blocLignes(lignes.map(katexInline), indent);
    } catch {
        return match;
    }
};

export const renderHtmlWithKatex = (input) => {
    if (typeof input !== 'string' || !input) return input || '';
    return input.split(BALISE_RE).map((morceau, i) => {
        if (i % 2 === 1) return morceau; // balise : intacte
        // Hors balise : les milliers du texte visible sont espacés (utils/milliers.js)
        // avant le repérage des expressions.
        return morceau.split(TEX_RE).map((part, j) => (
            j % 2 === 1 ? part.replace(MATH_REGEX, htmlTex) : espacerMilliersTexte(part).replace(RUN_RE, htmlRunTexte)
        )).join('');
    }).join('');
};

export const HtmlWithKatex = ({ html, className, as: Tag = 'span' }) => (
    <Tag
        className={className}
        dangerouslySetInnerHTML={{ __html: renderHtmlWithKatex(html) }}
    />
);
